import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  NoSymbolIcon,
  StarIcon as StarIconOutline,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconFilled } from "@heroicons/react/24/solid";
import { useState } from "react";
import { classNames } from "../common/functions";
import { useMiscSettings, useSearchResultsCache } from "../buyerStateDefs";
import { createPortal } from "react-dom";
import { useUser } from "../common/stateDefs";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";

const SearchResultFeedbackButtons = (props: {
  productId: string;
  onFeedbackGiven: (productId: string, feedback: string) => void;
}) => {
  const [feedbackGiven, setFeedbackGiven] = useState<string[]>([]);
  const handleFeedbackGiven = (feedback: string) => {
    setFeedbackGiven([...feedbackGiven, feedback]);
    props.onFeedbackGiven(props.productId, feedback);
  };

  return (
    <div className="flex space-x-2 mt-4">
      <button
        title="Should be ranked higher"
        onClick={() => handleFeedbackGiven("higher")}
      >
        <ArrowUpCircleIcon
          className={classNames(
            "h-6 w-6 text-green-600 hover:text-green-800",
            feedbackGiven.includes("higher") && "opacity-50",
          )}
        />
      </button>
      <button
        title="Should be ranked lower"
        onClick={() => handleFeedbackGiven("lower")}
      >
        <ArrowDownCircleIcon
          className={classNames(
            "h-6 w-6 text-red-600 hover:text-red-800",
            feedbackGiven.includes("lower") && "opacity-50",
          )}
        />
      </button>
      <button
        title="Not relevant"
        onClick={() => handleFeedbackGiven("not_relevant")}
      >
        <NoSymbolIcon
          className={classNames(
            "h-6 w-6 text-gray-600 hover:text-gray-800",
            feedbackGiven.includes("not_relevant") && "opacity-50",
          )}
        />
      </button>
    </div>
  );
};

const StarRatingInput = (props: {
  numStars: number;
  onRatingGiven: (rating: number, outOf: number) => void;
}) => {
  const [selectedRating, setSelectedRating] = useState<number>(0);

  const handleStarClick = (starIndex: number) => {
    setSelectedRating(starIndex);
    props.onRatingGiven(starIndex, props.numStars);
  };

  return (
    <div className="flex space-x-1">
      {[...Array(props.numStars)].map((_, index) => {
        const starIndex = index + 1;
        return (
          <button key={starIndex} onClick={() => handleStarClick(starIndex)}>
            {starIndex <= selectedRating ? (
              <StarIconFilled className="h-4 w-4 text-yellow-400 hover:text-yellow-500" />
            ) : (
              <StarIconOutline className="h-4 w-4 text-gray-400 hover:text-yellow-500" />
            )}
          </button>
        );
      })}
    </div>
  );
};

type StructuredFeedback = {
  feedbackType: "relevance" | "ranking" | "free_text" | "per_result";
  rankingQuality?: {
    rating: number;
    outOf: number;
  };
  freeText?: string;
  perResult?: {
    productId: string;
    feedback: string;
  };
};

const OverallFeedbackButtons = (props: {
  onFeedbackGiven: (feedback: StructuredFeedback) => void;
}) => {
  const [freeTextGiven, setFreeTextGiven] = useState<boolean>(false);
  const handleFeedbackGiven = (feedback: StructuredFeedback) => {
    props.onFeedbackGiven(feedback);
  };

  const solicitFreeTextFeedback = () => {
    const userInput = prompt("Please provide your notes:");
    if (userInput !== null && userInput.trim() !== "") {
      handleFeedbackGiven({
        feedbackType: "free_text",
        freeText: userInput.trim(),
      });
    }
    setFreeTextGiven(true);
  };

  return (
    <div className="flex space-x-1 mt-4 border rounded-full px-4 py-2 bg-gray-50 items-center">
      <div className="text-sm">Result relevance:</div>
      <StarRatingInput
        numStars={4}
        onRatingGiven={(rating, outOf) =>
          handleFeedbackGiven({
            feedbackType: "relevance",
            rankingQuality: { rating, outOf },
          })
        }
      />
      <div className="text-sm pl-4">Ranking quality:</div>
      <StarRatingInput
        numStars={4}
        onRatingGiven={(rating, outOf) =>
          handleFeedbackGiven({
            feedbackType: "ranking",
            rankingQuality: { rating, outOf },
          })
        }
      />
      <div className="text-sm pl-4">
        <button
          className="underline text-gray-600"
          onClick={solicitFreeTextFeedback}
        >
          Add notes
        </button>{" "}
        {freeTextGiven && "✓"}
      </div>
    </div>
  );
};

export const SearchFeedbackWrapper = () => {
  const searchResultsCache = useSearchResultsCache(
    (state) => state.searchResultsCache,
  );
  const user = useUser((state) => state.user);
  const showSearchFeedback = useMiscSettings(
    (state) => state.showSearchFeedbackElements,
  );

  if (!user || !showSearchFeedback || !searchResultsCache) {
    return null;
  }

  const recordFeedback = (feedback: StructuredFeedback) => {
    // save to Firebase collection "search-feedback"
    console.log("Recording feedback:", feedback);
    if (!user) {
      console.warn("No user logged in, cannot record feedback");
      alert("You must be logged in to submit feedback.");
      return;
    }

    addDoc(collection(db, "search-feedback"), {
      userId: user.id,
      timestamp: new Date(),
      searchResults: searchResultsCache,
      feedback: feedback,
    })
      .then(() => {
        console.log("Feedback recorded successfully");
      })
      .catch((error) => {
        alert("Error recording feedback, please try again.");
        console.error("Error recording feedback:", error);
      });
  };

  const handlePerResultFeedback = (productId: string, feedback: string) => {
    recordFeedback({
      feedbackType: "per_result",
      perResult: {
        productId,
        feedback,
      },
    });
  };

  const handleOverallFeedback = (feedback: StructuredFeedback) => {
    recordFeedback(feedback);
  };

  return (
    <>
      {(searchResultsCache?.results || []).map((result) => {
        const targetSearchResultDiv = document.getElementById(
          `search-result-annotations-${result.id}`,
        );

        if (targetSearchResultDiv) {
          return createPortal(
            <SearchResultFeedbackButtons
              key={result.id}
              productId={result.id}
              onFeedbackGiven={handlePerResultFeedback}
            />,
            targetSearchResultDiv,
          );
        }
        return null;
      })}
      {(() => {
        const overallContainer = document.getElementById(
          "search-overall-annotations-container",
        );

        if (overallContainer) {
          return createPortal(
            <OverallFeedbackButtons onFeedbackGiven={handleOverallFeedback} />,
            overallContainer,
          );
        }
        return null;
      })()}
    </>
  );
};
